/* eslint-disable prettier/prettier */
import { MonthRange, WeekRange } from '../types/schedule.types';
import {
	DAY_NAMES_SHORT,
	WEEK_DISPLAY_ORDER,
} from '../constants/activitytypes.constant';

export const MONTHS_LONG = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];
export const MONTHS_SHORT = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sept',
	'Oct',
	'Nov',
	'Dec',
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function getWeekStart(date: Date): Date {
	const d = new Date(date);
	const day = d.getDay(); // 0=Sun
	// Start on Monday
	const diff = day === 0 ? -6 : 1 - day;
	d.setDate(d.getDate() + diff);
	d.setHours(0, 0, 0, 0);
	return d;
}

export function getWeekEnd(date: Date): Date {
	const start = getWeekStart(date);
	const end = new Date(start);
	end.setDate(start.getDate() + 6);
	end.setHours(23, 59, 59, 999);
	return end;
}

export function getWeekRange(date: Date): WeekRange {
	return { start: getWeekStart(date), end: getWeekEnd(date) };
}

export function addWeeks(week: WeekRange, count: number): WeekRange {
	const d = new Date(week.start);
	d.setDate(d.getDate() + count * 7);
	return getWeekRange(d);
}

export function formatWeekRange(week: WeekRange): string {
	const { start, end } = week;
	const startDay = start.getDate();
	const endDay = end.getDate();
	const startMonth = MONTHS_LONG[start.getMonth()];
	const endMonth = MONTHS_LONG[end.getMonth()];
	const year = end.getFullYear();
	if (
		start.getMonth() === end.getMonth() &&
		start.getFullYear() === end.getFullYear()
	) {
		return `${startDay} – ${endDay} ${startMonth} ${year}`;
	}
	return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
}

export function getWeekDates(week: WeekRange): Date[] {
	// Returns Mon–Sun
	return WEEK_DISPLAY_ORDER.map(dayOfWeek => {
		const d = new Date(week.start);
		// week.start is Monday (getDay()===1)
		const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
		d.setDate(d.getDate() + diff);
		return d;
	});
}

export function getMonthRange(year: number, month: number): MonthRange {
	// new Date(year, month, ...) normalises month overflow (-1 -> Dec prev year)
	const firstOfMonth = new Date(year, month, 1);
	const lastOfMonth = new Date(year, month + 1, 0); // day 0 = last day of `month`

	const start = getWeekStart(firstOfMonth);
	const end = getWeekEnd(lastOfMonth);

	const startDayUTC = Date.UTC(
		start.getFullYear(),
		start.getMonth(),
		start.getDate()
	);
	const endDayUTC = Date.UTC(
		end.getFullYear(),
		end.getMonth(),
		end.getDate()
	);
	const weekCount = Math.round(
		(endDayUTC - startDayUTC + MS_PER_DAY) / (7 * MS_PER_DAY)
	);

	return {
		start,
		end,
		month: firstOfMonth.getMonth(),
		year: firstOfMonth.getFullYear(),
		weekCount,
	};
}

export function getMonthRangeFor(date: Date): MonthRange {
	return getMonthRange(date.getFullYear(), date.getMonth());
}

export function addMonths(range: MonthRange, count: number): MonthRange {
	return getMonthRange(range.year, range.month + count);
}

export function getMonthWeeks(range: MonthRange): WeekRange[] {
	const weeks: WeekRange[] = [];
	let cursor = new Date(range.start);
	for (let i = 0; i < range.weekCount; i++) {
		weeks.push(getWeekRange(cursor));
		cursor = new Date(cursor);
		cursor.setDate(cursor.getDate() + 7);
	}
	return weeks;
}

export function formatMonthRange(range: MonthRange): string {
	const { start, end } = range;
	const startDay = start.getDate();
	const endDay = end.getDate();
	const startMonth = MONTHS_LONG[start.getMonth()];
	const endMonth = MONTHS_LONG[end.getMonth()];
	const endYear = end.getFullYear();

	if (
		start.getMonth() === end.getMonth() &&
		start.getFullYear() === end.getFullYear()
	) {
		return `${startDay} – ${endDay} ${startMonth} ${endYear}`;
	}
	if (start.getFullYear() !== end.getFullYear()) {
		return `${startDay} ${startMonth} ${start.getFullYear()} – ${endDay} ${endMonth} ${endYear}`;
	}
	return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${endYear}`;
}

export function formatMonthName(range: MonthRange): string {
	return `${MONTHS_LONG[range.month]} ${range.year}`;
}

export function isSameMonthRange(a: MonthRange, b: MonthRange): boolean {
	return a.month === b.month && a.year === b.year;
}

export function isDateInRange(date: Date, range: WeekRange | null): boolean {
	if (!range) return false;
	return date >= range.start && date <= range.end;
}

export function toLocalTimeHM(isoString: string): string {
	const date = new Date(isoString);

	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');

	return `${hours}:${minutes}`;
}

export function toDateOnly(isoString: string): string {
	return toDateKey(new Date(isoString));
}

export function toDateKey(date: Date): string {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function isSameDay(a: Date, b: Date): boolean {
	return (
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	);
}

export function isToday(date: Date): boolean {
	return isSameDay(date, new Date());
}

export function getDayLabel(date: Date): string {
	const dow = date.getDay();
	return DAY_NAMES_SHORT[dow];
}

export function getCalendarDays(year: number, month: number): Date[] {
	const firstDay = new Date(year, month, 1);
	const lastDay = new Date(year, month + 1, 0);
	const days: Date[] = [];
	// Fill leading from prev month (Mon-start)
	let startDow = firstDay.getDay();
	startDow = startDow === 0 ? 6 : startDow - 1; // convert to Mon=0
	for (let i = startDow - 1; i >= 0; i--) {
		const d = new Date(firstDay);
		d.setDate(d.getDate() - (i + 1));
		days.push(d);
	}
	for (let d = 1; d <= lastDay.getDate(); d++) {
		days.push(new Date(year, month, d));
	}
	const remaining = 7 - (days.length % 7);
	if (remaining < 7) {
		for (let i = 1; i <= remaining; i++) {
			const d = new Date(lastDay);
			d.setDate(d.getDate() + i);
			days.push(d);
		}
	}
	return days;
}
